<template>
    <KForm
        ref="formRef"
        :id="formId"
        @cancel="cancelForm"
        :config-module-name="ConfigModuleName.Operation"
        :config-sub-module-name="OperationConfigModuleName.Anmalan"
        :initial-values-getter="initForm"
        :triggers="[kontaktorsakTrigger, ...formTriggers, ...typoperationFormTriggers]"
        :listeners="[
            ...(digitumDisplayRef?.formListeners ?? []),
            ...(bmiDisplayRef?.formListeners ?? []),
            ...(statusRef?.formListeners ?? []),
            ...(typoperationRef?.formListeners ?? []),
            statusListener,
        ]"
        :form-errors="formErrors"
        :errors="inputErrors"
        :showEmptyValues="true"
        formPadding="sections"
    >
        <template #header="{ formDataReference }">
            <h1>{{ operationsanmalanId ? "Redigera" : "Skapa" }} operationsanmälan</h1>
            <AnmalanInfoBar
                v-if="operationsanmalanId ? formDataReference?.created_at : createdBy"
                :creating-anmalan="!operationsanmalanId"
                :show-updated="!!operationsanmalanId"
                :creating-preliminary-data="creatingPreliminaryData"
                :anmalan="formDataReference"
            />
        </template>
        <template #default>
            <KSection name="kontaktinformation" :collapsible="false" :numbered="false">
                <div class="form-content">
                    <KontaktorsakAutocomplete
                        suffixText="Valet kan visa eller dölja fält"
                        :border="true"
                        variant="blue-background"
                    />

                    <TypoperationAutocomplete
                        ref="typoperationRef"
                        suffixText="Ändring av detta fält kan ersätta vissa värden"
                        :border="true"
                        variant="blue-background"
                        class="span-two"
                    />

                    <KDateTimepicker
                        name="documentTime"
                        label="Dokumentationsdatum"
                        validation="required"
                        convertTo="datetime"
                        :border="true"
                    />
                </div>
            </KSection>
            <KSection name="planering" heading="Planering" :collapsible="true" :numbered="true">
                <div class="form-content">
                    <OrgunitAutocomplete
                        name="operationsenhet"
                        label="Operationsenhet"
                        :urlParams="{ vardenhetstyp: 'operationsenhet', is_active: true, only_permitted: true }"
                        validation="required"
                    />

                    <!-- urlParams beror på den valda enheten och ligger därför i triggern -->
                    <ProfileAutocomplete
                        name="huvudoperator"
                        label="Huvudoperatör"
                        validation="required"
                        :retrieveOptionsOnMount="false"
                    />

                    <KCheckbox
                        name="huvudoperator_ej_bestamd"
                        label="Huvudoperatör ej bestämd"
                        valign="center"
                        :ignoreEmptyData="true"
                    />

                    <KCheckbox name="kort_varsel" label="Kort varsel" :ignoreEmptyData="true" valign="center" />

                    <ProfileRepeater
                        name="ass_operatorer"
                        label="Assisterande operatörer"
                        add_label="Lägg till assisterande op"
                        :urlParams="{ operator: true }"
                        class="span-two"
                    />

                    <KCheckbox
                        name="assoperator_ej_bestamd"
                        label="Assisterande operatör ej bestämd"
                        valign="center"
                        :ignoreEmptyData="true"
                    />

                    <PrioritetAutocomplete />

                    <KDateTimepicker name="preliminart_datum" label="Önskat operationsdatum" />

                    <AvtalAutocomplete />

                    <ForsakringsbolagAutocomplete />

                    <VardformAutocomplete validation="required" />

                    <KGroup
                        label="Slutenvårdsplanering"
                        name="slutenvardsplanering"
                        direction="vertical"
                        class="span-all"
                    >
                        <KNumber
                            name="antal_dagar_pa_avdelning"
                            label="Antal dygn på avdelningen"
                            :minValue="0"
                            :disabled="true"
                        />

                        <KNumber
                            name="inlaggning_antal_dagar_fore"
                            label="Inläggning antal dygn före op"
                            :minValue="0"
                        />

                        <KText
                            label="Info till vårdavdelningen"
                            name="info_till_vardavdelningen"
                            type="textarea"
                            class="span-two"
                        />
                    </KGroup>
                </div>
            </KSection>
            <KSection name="operation" heading="Operation" :collapsible="true" :numbered="true">
                <div class="form-content">
                    <HuvuddiagnoskodAutocomplete name="huvuddiagnos" class="span-three" />

                    <BidiagnoskodRepeater class="span-all" />

                    <HuvudoperationskodAutocomplete class="span-three" />

                    <BioperationskodRepeater class="span-all" />

                    <KGroup name="garantiop_reop" class="span-all">
                        <KCheckbox name="garantiop" label="Garantioperation" :ignoreEmptyData="true" valign="center" />

                        <KCheckbox name="reop" label="Reoperation" :ignoreEmptyData="true" valign="center" />
                    </KGroup>

                    <KGroup :shouldBeVisible="kontaktorsakVisible?.groups?.nivaer" name="nivaer" class="span-all">
                        <NivaerRepeater />
                    </KGroup>

                    <KGroup name="digitum" class="span-all" :shouldBeVisible="kontaktorsakVisible?.groups?.digitum">
                        <DigitumRepeater />

                        <DigitumDisplay ref="digitumDisplayRef" />
                    </KGroup>

                    <KGroup class="span-all" name="tider">
                        <KNumber
                            name="forberedelser"
                            label="Förberedelsetid (min)"
                            :minValue="0"
                            validation="required"
                        />

                        <KNumber name="optid" label="Operationstid (min)" :minValue="1" validation="required" />

                        <KNumber name="avveckling" label="Avvecklingstid (min)" :minValue="0" validation="required" />
                    </KGroup>

                    <PatientlageAutocomplete name="oplage" label="Patientläge" validation="required" />

                    <SidaAutocomplete name="lateralitet" validation="required" />

                    <AnestesiAutocomplete validation="required" />

                    <BlockadAutocomplete />

                    <ResursRepeater class="span-three" />

                    <ImplantatRepeater class="span-all" />

                    <StudieKvalitetsregisterRepeater
                        class="span-two"
                        :shouldBeVisible="kontaktorsakVisible?.fields?.studie_kvalitetsregister"
                    />

                    <KText
                        name="operationsinformation"
                        label="Info till operation"
                        type="textarea"
                        class="span-two"
                        :suffixText="'Visas i dags och veckoöversikt'"
                    />
                </div>
            </KSection>
            <KSection name="patientinformation" heading="Patientinformation" :collapsible="true" :numbered="true">
                <div class="form-content">
                    <KGroup name="rokare" class="span-all" :shouldBeVisible="kontaktorsakVisible?.groups?.rokare">
                        <JaNejAutocomplete
                            :includeOkand="true"
                            name="rokare"
                            label="Rökare"
                            :shouldBeVisible="kontaktorsakVisible?.fields?.rokare"
                        />
                        <JaNejAutocomplete
                            name="rokavvanjning"
                            label="Rökavvänjning har erbjudits"
                            :includeOkand="true"
                            :disabled="true"
                            :shouldBeVisible="kontaktorsakVisible?.fields?.rokavvanjning"
                        />
                        <JaNejAutocomplete
                            name="snus_och_andra_nikotinpreparat"
                            label="Snus och andra nikotinpreparat"
                        />
                    </KGroup>

                    <KNumber name="langd" label="Längd (cm)" :minValue="30" :maxValue="240" />

                    <KNumber name="vikt" label="Vikt (kg)" :maxValue="350" validation="decimals:1" />

                    <BMIDisplay ref="bmiDisplayRef" />

                    <KAutocomplete
                        name="invagning"
                        label="Invägning"
                        :options="[
                            { label: 'Ja', value: true },
                            { label: 'Nej', value: false },
                        ]"
                    />

                    <KNumber
                        name="viktminskning"
                        label="Önskad viktminskning (kg)"
                        :minValue="0.1"
                        :step="0.1"
                        validation="decimals:1"
                    />

                    <KGroup name="diabetes" class="span-two">
                        <KAutocomplete
                            name="diabetes"
                            label="Diabetes"
                            :options="[
                                { label: 'Ja', value: true },
                                { label: 'Nej', value: false },
                            ]"
                        />

                        <DiabetesBehandlingsformAutocomplete :disabled="true" />
                    </KGroup>

                    <JaNejAutocomplete name="giltig_halsodeklaration" label="Giltig hälsodeklaration" />

                    <KGroup name="blodtryck" class="span-two">
                        <KNumber name="blodtryck_systoliskt" label="Blodtryck syst" />

                        <KNumber name="blodtryck_diastoliskt" label="Blodtryck diast" />
                    </KGroup>

                    <JaNejAutocomplete name="blodsmitta" label="Blodsmitta" />

                    <JaNejAutocomplete name="multiresistenta_bakterier" label="Multiresistenta bakterier" />

                    <KGroup name="sprak" class="span-two">
                        <JaNejAutocomplete name="tolk" label="Tolk" />

                        <SprakAutocomplete :disabled="true" />
                    </KGroup>
                </div>
            </KSection>
            <KSection
                name="operationsforberedande_atgarder"
                heading="Operationsförberedande åtgärder"
                :collapsible="true"
                :numbered="true"
            >
                <div class="form-content">
                    <TrombosprofylaxAutocomplete :shouldBeVisible="kontaktorsakVisible?.fields?.trombosprofylax" />

                    <AntibiotikaprofylaxAutocomplete
                        :shouldBeVisible="kontaktorsakVisible?.fields?.antibiotikaprofylax"
                    />

                    <KGroup
                        name="antikoagulantia"
                        class="span-two"
                        :shouldBeVisible="kontaktorsakVisible?.groups?.antikoagulantia"
                    >
                        <JaNejAutocomplete
                            name="antikoagulantia"
                            label="Antikoagulantia sedan tidigare"
                            :shouldBeVisible="kontaktorsakVisible?.fields?.antikoagulantia"
                        />

                        <KNumber
                            name="satts_ut_dagar_innan"
                            label="Sätts ut dagar före op"
                            :minValue="1"
                            :disabled="true"
                            :shouldBeVisible="kontaktorsakVisible?.fields?.satts_ut_dagar_innan"
                        />
                    </KGroup>

                    <JaNejAutocomplete
                        name="nsaid"
                        label="NSAID får ges"
                        :shouldBeVisible="kontaktorsakVisible?.fields?.nsaid"
                    />

                    <RontgenAutocomplete name="rontgenbilder_till_op" label="Röntgenbilder till op" />

                    <KDateTimepicker
                        name="datum_rontgenbilder_till_op"
                        label="Datum för röntgenbilder till op"
                        :shouldBeVisible="kontaktorsakVisible?.fields?.datum_rontgenbilder_till_op"
                    />

                    <KText name="var_ar_rontgen_tagen" label="Var är röntgen tagen?" type="textarea" />

                    <RontgenAutocomplete name="ny_rontgen_infor_op" label="Ny röntgen inför op beställd" />

                    <KAutocomplete
                        name="blodgruppering"
                        label="Blodgruppering"
                        :options="[
                            { label: 'Ordineras', value: 'ORDINERAS' },
                            { label: 'Behövs ej', value: 'BEHOVS_EJ' },
                        ]"
                    />

                    <KAutocomplete
                        name="bastest"
                        label="Bastest"
                        :options="[
                            { label: 'Ordineras', value: 'ORDINERAS' },
                            { label: 'Behövs ej', value: 'BEHOVS_EJ' },
                        ]"
                    />

                    <KText
                        name="info_till_opplaneringen"
                        label="Info till operationskoordinator"
                        type="textarea"
                        class="span-two"
                        :suffixText="'Visas i operationskoordinering'"
                    />

                    <KText
                        name="info_till_koordinering_och_operation"
                        label="Info till operationskoordinator och operation"
                        type="textarea"
                        class="span-two"
                        :suffixText="'Visas i operationskoordinering och dagsöversikt'"
                    />
                </div>
            </KSection>

            <KSection
                name="postoperativa_atgarder"
                heading="Postoperativa åtgärder"
                :collapsible="true"
                :numbered="true"
            >
                <div class="form-content">
                    <HjalpmedelRepeater :direction-upwards="true" class="span-three" />
                    <KText
                        name="hjalpmedel_kommentar"
                        label="Hjälpmedel kommentar"
                        type="textarea"
                        class="span-three"
                    />
                </div>
            </KSection>

            <FormulateErrors />

            <ActionsStickyBarPortal
                :formRef="$refs.formRef"
                :form-id="formId"
                :tab-id="tabId || 0"
                @cancel="cancelForm"
                @submit="submitForm"
                :selectOptionsStatusar="STATUSAR"
                submit-size="secondary"
                cancel-size="small"
                :submit-content="operationsanmalanId ? 'Spara operationsanmälan' : 'Skicka anmälan till opkoordinator'"
                ref="statusRef"
                cancel-content="Avbryt"
                :disableSubmit="!can({ action: operationsanmalanId ? 'update' : 'create', subject: 'anmalan' })"
                :operationsanmalanId="operationsanmalanId"
            >
                <template #other-fields>
                    <KAutocomplete
                        v-if="can({ action: 'changestatus', subject: 'anmalan' })"
                        ref="autocompleteRef"
                        :class="{ 'direction-upwards': true }"
                        :container-classes="'no-margin-block'"
                        :registerOnMountId="`${formId}`"
                        :formRef="formRef"
                        label=""
                        name="status"
                        :options="selectOptionsStatusar"
                        @inputValue="onSelectStatusOption"
                        class="span-two actions-status"
                        :clearable="false"
                        :direction="'up'"
                    >
                        <template #option="{ option }">
                            <KAutocompleteOption
                                :content="option.label"
                                :use-icon="true"
                                :status-icon-key="option.value"
                            />
                        </template>
                        <template #selected-option="{ option }">
                            <KAutocompleteOption
                                :content="option.label"
                                :use-icon="true"
                                :status-icon-key="option.value"
                            />
                        </template>
                    </KAutocomplete>
                </template>
                <template #actions>
                    <Button
                        v-if="can({ action: 'boka', subject: 'anmalan' })"
                        size="slim"
                        variant="standard"
                        type="button"
                        :disabled="sendAndBookButtonDisabled"
                        @click="saveAndBook"
                    >
                        {{ operationsanmalanId ? "Spara och boka operation" : "Skicka anmälan och boka" }}
                    </Button>
                    <Button
                        v-if="
                            can({ action: operationsanmalanId ? 'update' : 'create', subject: 'anmalan' }) &&
                            submitAndPrintButtonVisible
                        "
                        size="slim"
                        variant="standard"
                        type="button"
                        @click="saveAndOpenOpinfo"
                    >
                        {{ operationsanmalanId ? "Spara och skriv ut" : "Skicka anmälan och skriv ut" }}
                    </Button>
                </template>
            </ActionsStickyBarPortal>
            <ResurskrockErrorKDialog
                @acceptResurskrock="submitWithResurskrock"
                ref="resurskrockDialogRef"
                :errors="resurskrockErrors"
            />
        </template>
    </KForm>
</template>

<script lang="ts">
    import tabIcon from "@/assets/operationsanmalan.svg";
    import BaseTab from "@/tabs/mixins/BaseTab";
    import ResurskrockErrorKDialog from "@/components/ResurskrockErrorKDialog.vue";

    export default {
        extends: BaseTab,
        name: "Operationsanmalan",
        tabName: "Operationsanmälan",
        icon: tabIcon,
        patientBound: true,
    };
</script>

<script setup lang="ts">
    import { typoperationFormTriggers } from "@/components/forms/reusables/TypoperationAutocomplete/anmalanHelpers";
    import { ref, computed, defineExpose, toRefs, onBeforeMount, onMounted, onBeforeUnmount, provide } from "vue";
    import BidiagnoskodRepeater from "@/components/forms/reusables/BidiagnoskodRepeater.vue";
    import BioperationskodRepeater from "@/components/forms/reusables/BioperationskodRepeater.vue";
    import DigitumRepeater from "@/components/forms/reusables/DigitumRepeater.vue";
    import NivaerRepeater from "@/components/forms/reusables/NivaerRepeater.vue";
    import HjalpmedelRepeater from "@/components/forms/reusables/HjalpmedelRepeater.vue";
    import HuvuddiagnoskodAutocomplete from "@/components/forms/reusables/HuvuddiagnoskodAutocomplete.vue";
    import HuvudoperationskodAutocomplete from "@/components/forms/reusables/HuvudoperationskodAutocomplete.vue";
    import ImplantatRepeater from "@/components/forms/reusables/ImplantatRepeater.vue";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KCheckbox from "@/components/forms/inputs/Checkbox/KCheckbox.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import OrgunitAutocomplete from "@/components/forms/reusables/OrgunitAutocomplete.vue";
    import ProfileAutocomplete from "@/components/forms/reusables/ProfileAutocomplete.vue";
    import ProfileRepeater from "@/components/forms/reusables/ProfileRepeater.vue";
    import ResursRepeater from "@/components/forms/reusables/ResursRepeater.vue";
    import emitter from "tiny-emitter/instance";
    import {
        ConfigModuleName,
        ConfigType,
        CoreConfigModuleName,
        OperationConfigModuleName,
    } from "@/store/modules/types/configModuleTypes";
    import { kontaktorsakDependents, STATUSAR } from "@/tabs/Operation/utils";
    import { cloneDeep } from "lodash";
    import { formTriggers } from "@/tabs/Operation/Operationsanmalan/FormTriggers";
    import { getConfig, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import { convertOperationskodData, Operationskod } from "@/store/modules/operation/operationskodModule";
    import { convertDiagnoskodData, Diagnoskod } from "@/store/modules/diagnoskodModule";
    import BlockadAutocomplete from "@/components/forms/reusables/BlockadAutocomplete.vue";
    import AnestesiAutocomplete from "@/components/forms/reusables/AnestesiAutocomplete.vue";
    import DiabetesBehandlingsformAutocomplete from "@/components/forms/reusables/DiabetesBehandlingsformAutocomplete.vue";
    import AvtalAutocomplete from "@/components/forms/reusables/AvtalAutocomplete.vue";
    import ForsakringsbolagAutocomplete from "@/components/forms/reusables/ForsakringsbolagAutocomplete.vue";
    import KontaktorsakAutocomplete from "@/components/forms/reusables/KontaktorsakAutocomplete.vue";
    import RontgenAutocomplete from "@/components/forms/reusables/RontgenAutocomplete.vue";
    import PatientlageAutocomplete from "@/components/forms/reusables/PatientlageAutocomplete.vue";
    import SprakAutocomplete from "@/components/forms/reusables/SprakAutocomplete.vue";
    import TrombosprofylaxAutocomplete from "@/components/forms/reusables/TrombosprofylaxAutocomplete.vue";
    import AntibiotikaprofylaxAutocomplete from "@/components/forms/reusables/AntibiotikaprofylaxAutocomplete.vue";
    import PrioritetAutocomplete from "@/components/forms/reusables/PrioritetAutocomplete.vue";
    import TypoperationAutocomplete from "@/components/forms/reusables/TypoperationAutocomplete/TypoperationAutocomplete.vue";
    import SidaAutocomplete from "@/components/forms/reusables/SidaAutocomplete.vue";
    import VardformAutocomplete from "@/components/forms/reusables/VardformAutocomplete.vue";
    import BMIDisplay from "@/components/forms/reusables/BMIDisplay.vue";
    import { convertDigitumData } from "@/components/forms/reusables/digitumHelper";
    import { ProfileType } from "@/types";
    import { convertProfileData } from "@/components/forms/reusables/profileHelper";
    import DigitumDisplay from "@/components/forms/reusables/DigitumDisplay.vue";
    import StudieKvalitetsregisterRepeater from "@/components/forms/reusables/StudieKvalitetsregisterRepeater.vue";
    import { convertAnestesiData } from "@/store/modules/operation/anestesiModule";
    import { convertBlockadData } from "@/store/modules/operation/blockadModule";
    import { convertAvtalData } from "@/store/modules/avtalModule";
    import { convertForsakringsbolagData } from "@/store/modules/forsakringsbolagModule";
    import moment from "moment";
    import { convertTypoperationData } from "@/store/modules/operation/typoperationModule";
    import { convertPrioritetData } from "@/components/forms/reusables/prioritetHelper";
    import { convertResursData, Resurs } from "@/components/forms/reusables/resurserHelper";
    import { convertHjalpmedelData, Hjalpmedel } from "@/components/forms/reusables/hjalpmedelHelper";
    import { convertImplantatData, Implantat } from "@/components/forms/reusables/implantatHelper";
    import {
        convertStudieKvalitetsregisterData,
        StudieKvalitetsregister,
    } from "@/components/forms/reusables/studieKvalitetsregisterHelper";
    import { convertVardformData } from "@/store/modules/operation/vardformModule";
    import { ActionReturn, Converter } from "@/components/forms/Form/types";
    import { Roller } from "@/utils/constants";
    import JaNejAutocomplete from "@/components/forms/reusables/JaNejAutocomplete.vue";
    import ActionsStickyBarPortal from "@/components/forms/reusables/ActionsStickyBarPortal.vue";
    import Button from "@/components/Button.vue";
    import dialogEvents from "@/dialogEvents";
    import BokaOperation from "@/tabs/Operation/BokaOperation.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import AnmalanInfoBar from "@/tabs/Operation/AnmalanInfoBar.vue";
    import KAutocompleteOption from "@/components/forms/inputs/Autocomplete/KAutocompleteOption.vue";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { addMinimumRows } from "@/components/forms/groups/Repeater/helpers";
    import { convertNivaData } from "@/components/forms/reusables/nivaerHelper";

    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";

    const props = defineProps({
        uid: Number,
        tabId: Number,
        parameters: Object,
        editing: Boolean,
    });

    const { parameters, tabId } = toRefs(props);

    const store = useStore();
    const { inputErrors, formErrors, parseErrorResponse, setErrors } = useKForm();

    provide("formParent", {
        inputErrors,
    });
    const can = store.getters["ability/can"];

    const currentProfile = computed(() => store.getters["userData/currentProfile"]);

    const operationsanmalanId = parameters?.value?.id ?? null;

    const formRef = ref();
    const bmiDisplayRef = ref();
    const digitumDisplayRef = ref();
    const typoperationRef = ref();

    const createdBy = ref("");
    const createdAt = ref("");
    const createdByRoll = ref("");
    const sendAndBookButtonDisabled = ref(false);
    const statusRef = ref();
    const selectOptionsStatusar = ref<typeof STATUSAR>(STATUSAR);
    /**
     * anmalanStatus behövs för att kolla om knappen "Spara och boka operation"/"Skicka opanmälan och boka" ska vara aktiv eller inte
     * och för att visa rätt status i headern
     */
    const anmalanStatus = ref("");
    const resurskrockDialogRef = ref();
    const resurskrockErrors = ref<Record<string, any>>({});
    const acceptResurskrock = ref(false);

    const statusListener = {
        fieldNames: ["status"],
        runAtInitial: true,
        action: ({ values }: { values: { status: string | { value: string } } }) => {
            sendAndBookButtonDisabled.value =
                values.status !== "Klar för operationsbokning" || !can({ action: "boka", subject: "anmalan" });
            anmalanStatus.value = values.status as string;
        },
    };

    const creatingPreliminaryData = computed(() => ({
        createdBy: createdBy.value,
        createdAt: createdAt.value,
        createdByRoll: createdByRoll.value,
        anmalanStatus: anmalanStatus.value,
    }));
    const defaultKontaktorsakVisible = computed<{
        fields: Record<string, boolean>;
        groups: Record<string, boolean>;
    }>(() => {
        const config = store.getters["config/configByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.Anmalan,
        });

        const result = {
            fields: {} as Record<string, boolean>,
            groups: {} as Record<string, boolean>,
        };

        kontaktorsakDependents.fields.forEach((field: { name: string }) => {
            result.fields[field.name] = config.fields[field.name]?.visible ?? true;
        });

        kontaktorsakDependents.groups.forEach((group: { name: string }) => {
            result.groups[group.name] = config.groups[group.name]?.visible ?? true;
        });

        return result;
    });

    const submitAndPrintButtonVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.Anmalan,
            type: ConfigType.Option,
            name: "submit_and_print_button",
        })
    );

    const kontaktorsakVisible = ref<{
        fields: Record<string, boolean>;
        groups: Record<string, boolean>;
    }>(defaultKontaktorsakVisible.value);

    const hasUnsavedData = computed(() => {
        return formRef.value?.hasUnsavedData();
    });

    const formId = "operationsanmalan-form";

    const autocompleteRef = ref();

    const onSelectStatusOption = ({ value }: { value: unknown }) => {
        const typedValue = value as Option;

        if (formRef?.value && autocompleteRef?.value) {
            (formRef?.value as { setFieldValue: (props: { value: Option; name: string }) => void }).setFieldValue({
                value: typedValue,
                name: "status",
            });
        }
    };

    defineExpose({
        hasUnsavedData,
    });

    onBeforeMount(async () => {
        if (!can({ action: "read", subject: "anmalan" })) {
            await store.dispatch("tabs/closeTab", tabId?.value);
        }
    });

    const initForm = async () => {
        await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: true });

        let formData: any = {};

        if (operationsanmalanId) {
            formData = await loadOperationsanmalan(operationsanmalanId);
        } else {
            formData.documentTime = moment().format("YYYY-MM-DD");
            createdBy.value = store.getters["userData/fullName"];
            createdAt.value = moment().format("YYYY-MM-DD");
            createdByRoll.value = currentProfile.value.roll_display?.displayName || "";

            if (currentProfile.value.roll?.code === Roller.KIRURG) {
                formData.operationsenhet = currentProfile.value.orgUnit.id;
                formData.huvudoperator = {
                    label: currentProfile.value.user_display.name,
                    value: currentProfile.value.pk,
                };
            }

            formData.forberedelser = 45;
            formData.avveckling = 15;

            formData.status = store.state.config?.operation?.OPERATION_ANMALAN_DEFAULT_STATUS;
            anmalanStatus.value = formData.status;
            autocompleteRef?.value?.setDisabled?.({ value: !can({ action: "changestatus", subject: "anmalan" }) });

            formData = {
                ...formData,
                ...getExtraData(),
            };
        }

        await store.dispatch("tabs/displaySpinner", { id: tabId?.value, display: false });

        return formData;
    };

    const submitForm = async (formData: any, closeTab: unknown = true) => {
        if (!can({ action: "create", subject: "anmalan" })) {
            return;
        }

        const data = {
            ...formData,
            digitum: formData.digitum ?? [],
            niva_fran_till: formData.niva_fran_till ?? [],
            studie_kvalitetsregister: formData.studie_kvalitetsregister ?? [],
        };

        // formData id blir null när vi skapar en ny anmälan, annars är det id från backend.
        data.id = operationsanmalanId;

        if (data.tolk !== "JA") {
            data.sprak = null;
        }

        if (!data.diabetes) {
            data.diabetes_lakemedelsform = null;
        }

        data.patient = store.getters["patientData/currentId"];

        if (acceptResurskrock.value) {
            data.overboka = true;
        }

        // kontrollera om det redan finns anmälan med samma uppgifter på patienten om en ny anmälan skapas
        if (!data.id) {
            try {
                // gör ett anrop till sökfunktionen i be
                const response = await klinikenApi.get(
                    "operation/anmalan/check_duplicate/",
                    getConfig({
                        params: {
                            patient: store.getters["patientData/currentId"],
                            huvuddiagnos: data.huvuddiagnos,
                            huvudoperationskod: data.huvudoperationskod,
                        },
                    })
                );

                if (response.data.duplicate) {
                    const result = await new Promise((resolve) => {
                        openDialog(
                            {
                                title: "Möjlig dubblett av anmälan",
                                description:
                                    "Det finns redan en operationsanmälan med samma huvudsakliga innehåll. Är du säker på att du vill fortsätta?",
                                buttons: [
                                    {
                                        title: "Skapa ny anmälan ändå",
                                        type: "primary",
                                        action: () => {
                                            resolve(true);
                                        },
                                    },
                                    {
                                        title: "Avbryt",
                                        type: "secondary",
                                        action: () => {
                                            resolve(false);
                                        },
                                    },
                                ],
                            },
                            "warning"
                        );
                    });

                    if (!result) {
                        return;
                    }
                }
            } catch (error) {
                openDialog("Ett fel inträffade vid kontroll av dubbletter. Försök igen senare.", "error");
                return;
            }
        }

        try {
            const resultId = await store.dispatch(
                operationsanmalanId
                    ? "operation/operationsanmalanV2/updateOperationsanmalan"
                    : "operation/operationsanmalanV2/createOperationsanmalan",
                {
                    operationsanmalan: data,
                    tabId: tabId?.value,
                    freezeAllSubsets: true,
                    config: {
                        tabbnamn: "Operationsanmälan",
                    },
                }
            );

            if (closeTab) {
                store.dispatch("tabs/closeTab", tabId?.value);
            }

            anmalanStatus.value = store.getters["operation/operationsanmalanV2/operationsanmalanById"]({
                id: resultId,
            }).status;

            return store.getters["operation/operationsanmalanV2/operationsanmalanById"]({ id: resultId });
        } catch (e: any) {
            let errors = parseErrorResponse(e);

            // Särskild hantering av non-field fel "overbokningar"
            if (errors?.nonFieldErrors?.overbokningar) {
                resurskrockErrors.value = { overbokningar: errors.nonFieldErrors.overbokningar };
                // ta bort overbokningar från errors
                const {
                    nonFieldErrors: { overbokningar, ...restNonFieldErrors },
                    ...rest
                } = errors;

                errors = {
                    ...rest,
                    nonFieldErrors: restNonFieldErrors,
                };
                resurskrockDialogRef.value?.show();
            }
            if (errors) {
                setErrors(errors);
            }
        } finally {
            acceptResurskrock.value = false;
        }
    };

    const saveAndBook = async () => {
        let formData = await formRef.value?.manualSubmit();
        if (!formData) {
            return;
        }
        try {
            const response = await submitForm(formData, false);
            if (response && (!formErrors.value || formErrors.value.length === 0)) {
                await bokaOperation(response);
                store.dispatch("tabs/closeTab", tabId?.value);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const saveAndOpenOpinfo = async () => {
        let formData = await formRef.value?.manualSubmit();
        if (!formData) {
            return;
        }
        try {
            const response = await submitForm(formData, true);
            if (response && (!formErrors.value || formErrors.value.length === 0)) {
                await store.dispatch("tabs/closeTab", Operationsinformation);
                store.dispatch("tabs/openTab", {
                    component: Operationsinformation,
                    parameters: { operationsanmalan_id: (response as { id: string }).id },
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const submitWithResurskrock = async () => {
        resurskrockErrors.value = {};
        let formData = await formRef.value?.manualSubmit();

        if (!formData) {
            return;
        }

        acceptResurskrock.value = true;
        await submitForm(formData, true);
    };

    // Använder Promise för att kunna invänta (await) resultatet från popupen.
    // Det gör att saveAndBook kan pausa tills användaren stänger eller bekräftar dialogen.
    // Utan detta skulle flödet fortsätta direkt utan att vänta på användarens val.
    const bokaOperation = async (item: any) => {
        return new Promise((resolve) =>
            dialogEvents.$emit(
                "openPopup",
                {
                    title: item.status !== "Bokad" ? "Boka operation" : "Redigera bokning",
                    component: BokaOperation,
                    dismissable: true,
                    data: {
                        listItem: item,
                    },
                },
                resolve
            )
        );
    };

    const cancelForm = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId?.value);
                        },
                    },
                ],
            });
        } else {
            store.dispatch("tabs/closeTab", tabId?.value);
        }
    };

    const loadOperationsanmalan = async (pk: string) => {
        try {
            const data = await store.dispatch("operation/operationsanmalanV2/getOperationsanmalanById", {
                id: pk,
                forceRefresh: true,
                config: {
                    tabbnamn: "Operationsanmälan",
                },
            });
            let formData = cloneDeep(data);

            formData = {
                ...formData,
                /**
                 * Repeaters - behöver initialiseras med extra tom rad om datan är tom (om du inte skickar ned
                 * minimumOneRow=false till repeatern)
                 */
                ass_operatorer: addMinimumRows(
                    formData.ass_operatorer_display?.map((item: ProfileType) => {
                        return { id: convertProfileData({ profile: item }) };
                    })
                ),
                bidiagnoser: addMinimumRows(
                    formData.bidiagnoser_display?.map((item: Diagnoskod) => {
                        return { id: convertDiagnoskodData({ diagnoskod: item }) };
                    })
                ),
                bioperationskoder: addMinimumRows(
                    formData.bioperationskoder_display?.map((item: Operationskod) => {
                        return {
                            id: convertOperationskodData({ operationskod: item }),
                        };
                    })
                ),
                digitum: addMinimumRows(
                    formData.digitum?.map((digit: string) => {
                        return {
                            id: convertDigitumData({ digit: digit }),
                        };
                    })
                ),
                niva_fran_till: addMinimumRows(
                    convertNivaData(formData.niva_fran_till, formData.niva_fran_till_display)
                ),
                resurser: addMinimumRows(
                    formData.resurser_display?.map((item: Resurs) => {
                        return {
                            id: convertResursData({ resurs: item }),
                        };
                    })
                ),
                implantat: addMinimumRows(
                    formData.implantat_display?.map((item: Implantat) => {
                        return {
                            id: convertImplantatData({ implantat: item }),
                        };
                    })
                ),
                studie_kvalitetsregister: addMinimumRows(
                    formData.studie_kvalitetsregister_display?.map((item: StudieKvalitetsregister) => {
                        return {
                            id: convertStudieKvalitetsregisterData({ studieKvalitetsregister: item }),
                        };
                    })
                ),
                hjalpmedel: addMinimumRows(
                    formData.hjalpmedel_display?.map((item: Hjalpmedel) => {
                        return {
                            id: convertHjalpmedelData({ hjalpmedel: item }),
                        };
                    })
                ),
                // Autocompletes
                prio: convertPrioritetData({ prioritet: formData.prio_display }),
                avtal: convertAvtalData({
                    avtal: formData.avtal_display,
                }),
                forsakringsbolag: convertForsakringsbolagData({
                    forsakringsbolag: formData.forsakringsbolag_display,
                }),
                typoperation: convertTypoperationData({
                    typoperation: formData.typoperation_display,
                }),
                planerad_vardform: convertVardformData({
                    vardform: formData.planerad_vardform_display,
                }),
                huvuddiagnos: convertDiagnoskodData({
                    diagnoskod: formData.huvuddiagnos_display,
                }),
                huvudoperationskod: convertOperationskodData({
                    operationskod: formData.huvudoperationskod_display,
                }),
                anestesi: convertAnestesiData({ anestesi: formData.anestesi_display }),
                blockad: formData.blockad_display ? convertBlockadData({ blockad: formData.blockad_display }) : null,
                huvudoperator: formData.huvudoperator_display
                    ? {
                          value: formData.huvudoperator_display.pk,
                          label: formData.huvudoperator_display.user_display.name,
                      }
                    : null,
            };

            createdBy.value = formData.created_by_display?.user_display?.name || "";
            createdAt.value = formData.created_at ? moment(formData.created_at).format("YYYY-MM-DD") : "";
            createdByRoll.value = formData.created_by_display?.roll_display.code || "";
            autocompleteRef?.value?.setDisabled?.({
                value:
                    !can({ action: "changestatus", subject: "anmalan" }) ||
                    ["Bokad", "Pågående", "Struken"].includes(formData.status),
            });

            return formData;
        } catch (e) {
            console.error(e);
            openDialog("Kunde inte hämta operationsanmälan.", "error");
        }
    };

    const getExtraData = () => {
        const extraData = store.getters["patientData/extraData"] ?? [];

        const patientExtraDataValidMonths = store.getters["config/configByName"]({
            moduleName: ConfigModuleName.Core,
            subModuleName: CoreConfigModuleName.PatientExtraDataValidMonths,
        });

        return Object.fromEntries(
            extraData
                ?.filter((item: { field: string; value: string | number; date: Date }) => {
                    return !!item.value && moment().subtract(patientExtraDataValidMonths, "month").isBefore(item.date);
                })
                .map((item: { field: string; value: string | number; date: Date }) => {
                    return [item.field, item.value];
                })
        );
    };

    /**
     * Bygger upp arrayen med objekt som returneras från kontaktorsakTrigger
     * Returnerar en array av ActionReturn
     */
    const convertToActionData = ({
        currentKontaktorsakVisible,
        value,
        config,
    }: {
        currentKontaktorsakVisible: any;
        value: any;
        config: {
            fields: Record<string, { visible?: boolean; required?: boolean }>;
            groups: Record<string, { visible?: boolean }>;
        };
    }): Array<ActionReturn> => {
        return Object.entries(config.fields).map(([fieldName, fieldConfig]) => {
            // required ska inte förändras på fält som är specade att de inte kan användas av användare via inställningar
            const required = kontaktorsakDependents.fields.find(({ name }) => name === fieldName)?.required
                ? (fieldConfig?.required ?? false)
                : undefined;

            // Om det är ett fält som ändras visibility ska det inte få sitt gamla värde satt igen
            if (getVisibleToggledFields({ currentKontaktorsakVisible, config }).includes(fieldName)) {
                return {
                    fieldName,
                    required,
                    value: null,
                };
            }

            return {
                fieldName,
                required,
                value: value[fieldName] ?? null,
            };
        });
    };

    /**
     * Konverterar från formatet som finns i config på kontaktorsak till formatet som formuläret behöver.
     * Returnerar fält och grupper som ska visas/döljas
     */
    const converterVisible = ({
        config,
    }: {
        config: {
            fields: Record<string, { visible?: boolean; required?: boolean }>;
            groups: Record<string, { visible?: boolean }>;
        };
    }): {
        fields: Record<string, boolean>;
        groups: Record<string, boolean>;
    } => {
        const result = {
            fields: {} as Record<string, boolean>,
            groups: {} as Record<string, boolean>,
        };

        // konverterar fält från config
        Object.keys(config.fields).forEach((key) => {
            result.fields[key] = config.fields[key].visible ?? true;
        });

        // lägger till fält som inte finns i config
        Object.keys(defaultKontaktorsakVisible.value.fields).forEach((key) => {
            if (!(key in result.fields)) {
                result.fields[key] = defaultKontaktorsakVisible.value.fields[key] ?? true;
            }
        });

        // konverterar grupper från config
        Object.keys(config.groups).forEach((key) => {
            result.groups[key] = config.groups[key].visible ?? true;
        });

        // lägger till grupper som inte finns i config
        Object.keys(defaultKontaktorsakVisible.value.groups).forEach((key) => {
            if (!(key in result.groups)) {
                result.groups[key] = defaultKontaktorsakVisible.value.groups[key] ?? true;
            }
        });

        return result;
    };

    /**
     * Returnerar namn på fält som ändrats från att vara synliga till dolda eller tvärtom
     */
    const getVisibleToggledFields = ({
        currentKontaktorsakVisible,
        config,
    }: {
        currentKontaktorsakVisible: any;
        config: {
            fields: Record<string, { visible?: boolean; required?: boolean }>;
            groups: Record<string, { visible?: boolean }>;
        };
    }) => {
        const newVisible = converterVisible({ config });
        const filteredFields = Object.entries(newVisible.fields).filter(([key, visible]) => {
            return currentKontaktorsakVisible.fields[key] !== visible;
        });
        const filteredGroupFields = Object.entries(newVisible.groups)
            .filter(([key, visible]) => {
                return currentKontaktorsakVisible.groups[key] !== visible;
            })
            .map(([key]) => {
                return kontaktorsakDependents.groups.find(({ name }) => name === key)?.fields ?? [];
            });

        return [...filteredFields, ...filteredGroupFields].map(([key]) => key);
    };

    /**
     * Nullar fält som ändrats från att vara synliga till dolda eller tvärtom för att se till att check
     * av osparad data och att borttag av värde i BE görs rätt
     */
    const setNullOnToggledFields = ({
        currentKontaktorsakVisible,
        config,
    }: {
        currentKontaktorsakVisible: any;
        config: {
            fields: Record<string, { visible?: boolean; required?: boolean }>;
            groups: Record<string, { visible?: boolean }>;
        };
    }) => {
        const fields = getVisibleToggledFields({ currentKontaktorsakVisible, config });

        formRef?.value?.nullFieldValues?.({
            fields,
        });
    };

    /* TODO Förslag är att göra om FormTrigger.ts till en composable och lägga in den här där så kan den returnera
        triggers, kontaktorsakVisible och annat. */
    /**
     * När kontaktorsak sätts ska fält döljas och/eller sättas som required. Fält som döljs får värde null för
     * att backend ska kunna rensa dem.
     */
    const kontaktorsakTrigger = {
        fieldName: kontaktorsakDependents.fields.map((field) => field.name),
        dependentOn: "kontaktorsak",
        action: async ({
            value,
            dependentOnValue,
            selectedOption,
        }: {
            value: any;
            dependentOnValue: any;
            selectedOption: any;
        }) => {
            // Vilka fält var synliga innan bytet av kontaktorsak
            const currentKontaktorsakVisible = kontaktorsakVisible.value;

            // config kan hämtas ut på olika sätt beroende på hur värdet på kontaktorsak kommer in i funktionen
            let localConfig;

            if (dependentOnValue?.config) {
                /* dependentOnValue kan vara ett objekt vid laddning av data i formuläret */
                localConfig = dependentOnValue?.config;
            } else if (selectedOption?.length > 0 && selectedOption[0]?.config) {
                /* om det valda värdet finns i selectedOption */
                localConfig = selectedOption[0].config;
            } else if (dependentOnValue) {
                /* dependentOnValue är ett id  */
                const kontaktorsak = await store.dispatch("operation/kontaktorsak/getKontaktorsakById", {
                    id: dependentOnValue,
                });

                localConfig = kontaktorsak?.config;
            }

            // Om vi har en vald kontaktorsak som har en config
            if (localConfig) {
                // Sätter vilka fält och grupper som ska vara synliga eller ej
                kontaktorsakVisible.value = converterVisible({ config: localConfig });

                // Tömmer fälten som har ändrat synlighet
                setNullOnToggledFields({ currentKontaktorsakVisible, config: localConfig });

                // Bygger upp array med objekt som ska returneras från triggers
                return convertToActionData({ currentKontaktorsakVisible, value, config: localConfig });
            }

            // Om det inte finns en vald kontaktorsak som har en config så används standardinställningarna
            // Hämtar grundinställningar för fälten
            const config = store.getters["config/configByName"]({
                moduleName: ConfigModuleName.Operation,
                subModuleName: OperationConfigModuleName.Anmalan,
            });

            // Sätter vilka fält och grupper som ska vara synliga eller ej
            kontaktorsakVisible.value = defaultKontaktorsakVisible.value;

            // Tömmer fälten som har ändrat synlighet
            setNullOnToggledFields({ currentKontaktorsakVisible, config: defaultKontaktorsakVisible.value as any });

            // Bygger upp array med objekt som ska returneras från triggers
            return kontaktorsakDependents.fields.map((field) => {
                // Om det är ett fält som ändras visibility ska det inte få sitt gamla värde satt igen
                if (
                    getVisibleToggledFields({
                        currentKontaktorsakVisible,
                        config: defaultKontaktorsakVisible.value as any,
                    }).includes(field.name)
                ) {
                    return {
                        fieldName: field.name,
                        required: config.fields[field.name]?.required ?? false,
                        value: null,
                    };
                }

                return {
                    fieldName: field.name,
                    required: config.fields[field.name]?.required ?? false,
                    value: value[field.name] ?? null,
                };
            });
        },
    };

    onMounted(() => {
        store.dispatch("operation/operationsanmalanV2/addSubscriber");
    });

    onBeforeUnmount(() => {
        store.dispatch("operation/operationsanmalanV2/removeSubscriber");
    });
</script>

<style lang="scss" scoped>
    .actions-status {
        min-width: 335px;
    }
</style>
